import { clsx } from 'clsx';
import { FC } from 'react';
import ReactMarkdown from 'react-markdown';

type MarkdownBodyProps = {
  content: string;
  /*
   * Renders a single newline inside a paragraph as a line break.
   *
   * Off by default, because Markdown is right to collapse one: an answer is
   * written as prose and wrapped wherever the line ran out, so honouring those
   * breaks would cut its sentences at arbitrary points.
   *
   * A question is not written that way. The user pressed Shift+Enter to put
   * the next line where it is, and collapsing that runs their lines together
   * in the bubble - so the composer's own hint stops being true the moment the
   * message is sent. Only what the user typed asks for this.
   */
  preserveLineBreaks?: boolean;
};

export const MarkdownBody: FC<MarkdownBodyProps> = ({ content, preserveLineBreaks = false }) => (
  <ReactMarkdown
    components={{
      /*
       * pre-line rather than pre-wrap: it keeps the newlines and still
       * collapses runs of spaces, which a paragraph has no reason to hold on
       * to - Markdown's own indentation among them.
       */
      p: ({ children }) => (
        <p
          className={clsx(
            'break-words mb-2 last:mb-0',
            preserveLineBreaks && 'whitespace-pre-line',
          )}
        >
          {children}
        </p>
      ),
      ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
      li: ({ children }) => <li className="break-words">{children}</li>,
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      h1: ({ children }) => <h1 className="text-lg font-bold mb-2 break-words">{children}</h1>,
      h2: ({ children }) => <h2 className="text-base font-bold mb-2 break-words">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-bold mb-1 break-words">{children}</h3>,
      /*
       * The one block that keeps its own line breaks, so it scrolls instead of
       * wrapping. Everything else here wraps, but an answer reaches for a fence
       * precisely when its lines are structural - CSV rows, a table, a schedule
       * - and a wrapped row stops lining up with the header that explains it.
       *
       * Without this it inherits `white-space: pre` with no way to scroll, and
       * a single long row runs out past the bubble's edge and off the panel.
       */
      pre: ({ children }) => (
        <pre className="mb-2 max-w-full overflow-x-auto last:mb-0">{children}</pre>
      ),
      /*
       * Reaches inline code only: inside a <pre> nothing may wrap at all, so
       * `overflow-wrap` has nothing to act on there. Inline, it stops a long id
       * or path running past the same edge.
       */
      code: ({ children }) => <code className="break-words">{children}</code>,
    }}
  >
    {content}
  </ReactMarkdown>
);
